import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { VerifiedOutlined } from "@mui/icons-material";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import Help from "@mui/icons-material/Help";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReportIcon from "@mui/icons-material/Report";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Tooltip,
} from "@mui/material";
import Typography from "@mui/material/Typography";

import ExtensionActions from "@/pages/browser-defender/extensionGovernance/ExtensionActions";
import ExtensionRisksList from "@/pages/browser-defender/extensionGovernance/ExtensionRisksList";
import PermissionsChip from "@/pages/browser-defender/extensionGovernance/PermissionsChip";
import UsersTable from "@/pages/browser-defender/extensionGovernance/UsersTable";

const riskColors = {
  high: {
    bgColor: "#FEE9EF8C",
    textColor: "#F6285F",
    icon: <WarningAmberIcon sx={{ color: "#F6285F" }} />,
  },
  medium: {
    bgColor: "#FEF5ED8C",
    textColor: "#F2994A",
    icon: <ErrorOutlineOutlinedIcon sx={{ color: "#F2994A" }} />,
  },
  low: {
    bgColor: "#1dc9b71a",
    textColor: "#1dc9b7",
    icon: <CheckCircleOutlineOutlinedIcon sx={{ color: "#1dc9b7" }} />,
  },
};

function ExtensionCardHeader({
  row,
  currentTab,
  onAllowExtension,
  onBlockExtension,
}) {
  const { t } = useTranslation();

  const [displayFullDescription, setDisplayFullDescription] = useState(false);

  useEffect(() => {
    setDisplayFullDescription(false);
  }, [row]);

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Grid
            container
            direction="column"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Grid item>
              <Box style={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  src={row.icon}
                  variant="rounded"
                  style={{
                    color: "rgb(6, 17, 35)",
                    fontWeight: "bold",
                    width: 60,
                    height: 60,
                  }}
                >
                  {row.name.charAt(0).toUpperCase()}
                </Avatar>
                <Box style={{ marginLeft: "1rem" }}>
                  <Typography
                    style={{
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {row.potentially_dangerous && (
                      <Tooltip title="Mantra suggests this extension might be dangerous">
                        <ReportIcon
                          style={{ color: "#F6285F", marginRight: "0.2rem" }}
                        />
                      </Tooltip>
                    )}
                    {row.name.charAt(0).toUpperCase() + row.name.slice(1)}
                  </Typography>
                  <Typography
                    style={{
                      color: "#7F8995",
                      fontSize: "1rem",
                    }}
                  >
                    {row.is_verified_publisher && (
                      <Tooltip title="Editeur vérifié">
                        <VerifiedOutlined
                          sx={{
                            fontSize: "1.3rem",
                            verticalAlign: "text-top",
                            marginRight: "2px",
                          }}
                        />
                      </Tooltip>
                    )}
                    {row.publisher || t("Unknown")}
                  </Typography>
                  <Box
                    style={{
                      marginTop: "0.3rem",
                      color: "#7F8995",
                      fontSize: "0.9rem",
                    }}
                  >
                    <Typography
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                      }}
                    >
                      {row.number_of_users && row.number_of_users > 0 && (
                        <>
                          {row.number_of_users.toLocaleString()} {t("users")}
                        </>
                      )}
                      {row.number_of_users &&
                        row.number_of_users > 0 &&
                        row.rating !== undefined &&
                        row.rating !== null && (
                          <span style={{ fontSize: "1.2rem", lineHeight: 0 }}>
                            ·
                          </span>
                        )}
                      {row.rating !== undefined && row.rating !== null && (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "text-top",
                            gap: "0.25rem",
                          }}
                        >
                          {row.rating.toFixed(1)}
                          {" ★ "}({row.number_of_ratings.toLocaleString()})
                        </span>
                      )}
                    </Typography>
                  </Box>
                </Box>
                {(row.homepage_url || row.store_url) && (
                  <Tooltip title={t("Visit homepage")}>
                    <Link
                      to={row.homepage_url || row.store_url}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <OpenInNewIcon
                        sx={{ color: "#5867dd", marginLeft: "1rem" }}
                      />
                    </Link>
                  </Tooltip>
                )}
              </Box>
            </Grid>
          </Grid>

          {row.category && (
            <Box sx={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
              <Box
                sx={{
                  backgroundColor: "#F0F0F0",
                  padding: "0.4rem",
                  borderRadius: "4px",
                  marginTop: "1rem",
                  display: "inline-block",
                }}
              >
                <Typography sx={{ fontSize: "0.9rem", color: "#333" }}>
                  {row.type.charAt(0).toUpperCase() + row.type.slice(1)}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: "#F0F0F0",
                  padding: "0.4rem",
                  borderRadius: "4px",
                  marginTop: "1rem",
                  display: "inline-block",
                }}
              >
                <Typography sx={{ fontSize: "0.9rem", color: "#333" }}>
                  {row.category.charAt(0).toUpperCase() + row.category.slice(1)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ExtensionActions
            selectedExtension={row}
            currentTab={currentTab}
            onAllow={onAllowExtension}
            onBlock={onBlockExtension}
          />
          {!row.last_check_successful && (
            <Tooltip
              title={t(
                "Last check failed, some information may not be up to date.",
              )}
            >
              <ErrorOutlineOutlinedIcon
                sx={{
                  color: "#F6285F",
                  fontSize: "2rem",
                  ml: 1,
                }}
              />
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box style={{ paddingTop: "1rem" }}>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          <Grid item xs>
            <Typography style={{ color: "#7F8995" }}>
              {displayFullDescription
                ? row.description
                : row.short_description || row.description?.split(/[.\n]/)[0]}
            </Typography>

            {(row.short_description || row.description?.split(/[.\n]/)[0]) !==
              row.description && (
              <Box mt={1}>
                <Typography
                  variant="body2"
                  sx={{ color: "#5867dd", cursor: "pointer" }}
                  onClick={() => setDisplayFullDescription((prev) => !prev)}
                >
                  {displayFullDescription ? t("See less") : t("See more")}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default function ExtensionDetailsCard({
  row,
  currentTab,
  onAllowExtension,
  onBlockExtension,
}) {
  const [data, setData] = useState(row);
  const { t } = useTranslation();

  const permissionRiskMessages = data.risk_details.map(
    ({ explanation }) => explanation,
  );
  const uniquePermissionRiskMessages = data.risk_details.filter(
    ({ explanation }, index) =>
      !permissionRiskMessages.includes(explanation, index + 1),
  );

  const riskScoreMessages = data.risk_score_details.map(
    ({ explanation }) => explanation,
  );
  const uniqueRiskScoreMessages = data.risk_score_details.filter(
    ({ explanation }, index) =>
      !riskScoreMessages.includes(explanation, index + 1),
  );

  useEffect(() => {
    setData(row);
    document.getElementById("scrolling_extension_details").scrollTo(0, 0);
  }, [row]);

  return (
    <Card sx={{ height: "100%", overflowY: "auto" }}>
      <CardHeader
        title={
          <ExtensionCardHeader
            row={data}
            currentTab={currentTab}
            onAllowExtension={onAllowExtension}
            onBlockExtension={onBlockExtension}
          />
        }
      />
      <Divider />
      <CardContent
        style={{
          display: "flex",
          flexFlow: "column",
          paddingTop: "1rem",
          flexGrow: 1,
        }}
        id="scrolling_extension_details"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            marginBottom: "2rem",
            marginTop: "2rem",
            columnGap: "2rem",
          }}
        >
          {data.last_check_date && (
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-start",
                  marginBottom: "1rem",
                  alignItems: "center",
                }}
              >
                <Typography
                  style={{
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    flexShrink: 0,
                  }}
                >
                  {t("Warnings")}
                </Typography>
                <Tooltip
                  title={t(
                    "Reasons this extension may be considered risky (e.g., suspicious publisher, rare updates, excessive permissions, low ratings). The badge color reflects the highest severity level among the warnings.",
                  )}
                >
                  <Help
                    sx={{
                      fontSize: 16,
                      marginLeft: 0.5,
                      color: "rgba(0, 0, 0, 0.5)",
                    }}
                  />
                </Tooltip>
              </Box>

              <Box>
                <Typography>
                  {uniqueRiskScoreMessages.length === 0 &&
                    t("This extension does not present any apparent risks!")}
                </Typography>
                <ExtensionRisksList
                  risks={uniqueRiskScoreMessages}
                  riskColors={riskColors}
                />
              </Box>
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                marginBottom: "1rem",
              }}
            >
              <Typography
                style={{
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  flexShrink: 0,
                  marginRight: "1rem",
                }}
              >
                {t("Permissions")}
              </Typography>

              <PermissionsChip
                risk={data}
                colors={riskColors}
                riskColors={riskColors}
              />
            </Box>

            <Box>
              <Typography>
                {uniquePermissionRiskMessages.length > 0
                  ? null
                  : data.permissions.length > 0
                    ? t(
                        "This extension requires minimally intrusive permissions only.",
                      )
                    : t("This extension requires no permissions.")}
              </Typography>
              <ExtensionRisksList
                risks={uniquePermissionRiskMessages}
                riskColors={riskColors}
              />
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ marginBottom: "2rem", marginTop: "2rem" }}>
          <Typography
            style={{
              fontWeight: "bold",
              fontSize: "1.1rem",
              marginBottom: "2rem",
            }}
          >
            {t("User details")}
          </Typography>

          <UsersTable
            users={data.users}
            riskColors={riskColors}
            extensionData={data}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
